import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { format } from 'date-fns';
import { Squad } from '../types';

interface GenerateCoachReportOptions {
  sessions: any[];
  coachStats: any[];
  startDate: string;
  endDate: string;
  coachId?: string;
}

interface GeneratePlayerReportOptions {
  sessions: any[];
  playerStats: any[];
  startDate: string;
  endDate: string;
  selectedSquads: Squad[];
  separateReports: boolean;
}

// Add logo to PDF
const addLogoToPDF = (doc: jsPDF) => {
  const logoUrl = 'https://gcajrvfuujmqkipzkvlw.supabase.co/storage/v1/object/public/mbfc-data//MBFC%20Logo.png';
  const logoSize = 20;
  doc.addImage(logoUrl, 'PNG', doc.internal.pageSize.width - 30, 10, logoSize, logoSize);
};

export const generateCoachAttendanceReport = ({
  sessions,
  coachStats,
  startDate,
  endDate,
  coachId
}: GenerateCoachReportOptions): void => {
  // Create new PDF document
  const doc = new jsPDF();
  
  // Add logo
  addLogoToPDF(doc);
  
  // Add header
  doc.setFontSize(20);
  doc.setTextColor(0, 40, 85); // Navy blue
  doc.text('Maverick Blues FC', 105, 15, { align: 'center' });
  
  doc.setFontSize(16);
  doc.text('Coach Attendance Report', 105, 25, { align: 'center' });
  
  doc.setFontSize(12);
  doc.setTextColor(100, 100, 100);
  doc.text(`Date Range: ${format(new Date(startDate), 'MMM d, yyyy')} - ${format(new Date(endDate), 'MMM d, yyyy')}`, 105, 35, { align: 'center' });
  
  let yPosition = 45;

  if (sessions.length === 0) {
    doc.setTextColor(100, 100, 100);
    doc.text('No sessions found for the selected criteria.', 105, yPosition, { align: 'center' });
    doc.save(`MBFC_coach_attendance_${format(new Date(), 'yyyyMMdd')}.pdf`);
    return;
  }

  // Add summary statistics
  const totalSessions = sessions.length;
  const totalHours = sessions.reduce((sum, session) => sum + session.duration, 0);
  
  doc.setFontSize(14);
  doc.setTextColor(0, 40, 85);
  doc.text('Summary Statistics', 14, yPosition);
  
  yPosition += 10;
  
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text(`Total Sessions: ${totalSessions}`, 14, yPosition);
  yPosition += 8;
  doc.text(`Total Training Hours: ${totalHours.toFixed(1)}`, 14, yPosition);
  
  yPosition += 15;

  // Add session details table
  doc.setFontSize(14);
  doc.setTextColor(0, 40, 85);
  doc.text('Session Details', 14, yPosition);
  
  yPosition += 10;

  const tableData = sessions.map(session => [
    format(new Date(session.date), 'MMM d, yyyy'),
    session.time,
    `${session.duration} hours`,
    session.location,
    session.session_coaches.map((sc: any) => sc.squads.name).join(', ')
  ]);

  // @ts-ignore - jspdf-autotable types
  doc.autoTable({
    startY: yPosition,
    head: [['Date', 'Time', 'Duration', 'Location', 'Squads']],
    body: tableData,
    headStyles: { fillColor: [0, 40, 85], textColor: [255, 255, 255] },
    alternateRowStyles: { fillColor: [240, 240, 240] },
    margin: { top: 10 }
  });

  // Save the PDF
  doc.save(`MBFC_coach_attendance_${format(new Date(), 'yyyyMMdd')}.pdf`);
};

export const generatePlayerAttendanceReport = ({
  sessions,
  playerStats,
  startDate,
  endDate,
  selectedSquads,
  separateReports
}: GeneratePlayerReportOptions): void => {
  if (separateReports) {
    // Generate separate reports for each squad
    selectedSquads.forEach(squad => {
      const squadStats = playerStats.find(s => s.squad === squad);
      if (squadStats) {
        generateSquadAttendanceReport(sessions, squadStats, startDate, endDate);
      }
    });
  } else {
    // Generate combined report
    generateCombinedAttendanceReport(sessions, playerStats, startDate, endDate, selectedSquads);
  }
};

const generateSquadAttendanceReport = (
  sessions: any[],
  squadStats: any,
  startDate: string,
  endDate: string
): void => {
  // Create new PDF document
  const doc = new jsPDF();
  
  // Add logo
  addLogoToPDF(doc);
  
  // Add header
  doc.setFontSize(20);
  doc.setTextColor(0, 40, 85);
  doc.text('Maverick Blues FC', 105, 15, { align: 'center' });
  
  doc.setFontSize(16);
  doc.text(`${squadStats.squad} Squad - Attendance Report`, 105, 25, { align: 'center' });
  
  doc.setFontSize(12);
  doc.setTextColor(100, 100, 100);
  doc.text(`Date Range: ${format(new Date(startDate), 'MMM d, yyyy')} - ${format(new Date(endDate), 'MMM d, yyyy')}`, 105, 35, { align: 'center' });
  
  let yPosition = 45;

  // Add summary statistics
  doc.setFontSize(14);
  doc.setTextColor(0, 40, 85);
  doc.text('Summary Statistics', 14, yPosition);
  
  yPosition += 10;
  
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text(`Total Sessions: ${squadStats.stats.totalSessions}`, 14, yPosition);
  yPosition += 8;
  doc.text(`Total Training Hours: ${squadStats.stats.totalHours.toFixed(1)}`, 14, yPosition);
  yPosition += 8;
  doc.text(`Average Attendance: ${((squadStats.stats.presentSessions / squadStats.stats.totalSessions) * 100).toFixed(1)}%`, 14, yPosition);
  
  yPosition += 15;

  // Add player attendance table
  doc.setFontSize(14);
  doc.setTextColor(0, 40, 85);
  doc.text('Player Attendance', 14, yPosition);
  
  yPosition += 10;

  const tableData = squadStats.stats.players.map((player: any) => [
    player.name,
    player.present,
    player.absent,
    `${((player.present / (player.present + player.absent)) * 100).toFixed(1)}%`,
    `${player.totalHours.toFixed(1)} hours`
  ]);

  // @ts-ignore - jspdf-autotable types
  doc.autoTable({
    startY: yPosition,
    head: [['Player Name', 'Present', 'Absent', 'Attendance %', 'Training Hours']],
    body: tableData,
    headStyles: { fillColor: [0, 40, 85], textColor: [255, 255, 255] },
    alternateRowStyles: { fillColor: [240, 240, 240] },
    margin: { top: 10 }
  });

  // Save the PDF
  doc.save(`MBFC_${squadStats.squad}_attendance_${format(new Date(), 'yyyyMMdd')}.pdf`);
};

const generateCombinedAttendanceReport = (
  sessions: any[],
  playerStats: any[],
  startDate: string,
  endDate: string,
  selectedSquads: Squad[]
): void => {
  // Create new PDF document
  const doc = new jsPDF();
  
  // Add logo
  addLogoToPDF(doc);
  
  // Add header
  doc.setFontSize(20);
  doc.setTextColor(0, 40, 85);
  doc.text('Maverick Blues FC', 105, 15, { align: 'center' });
  
  doc.setFontSize(16);
  doc.text('Combined Attendance Report', 105, 25, { align: 'center' });
  
  doc.setFontSize(12);
  doc.setTextColor(100, 100, 100);
  doc.text(`Date Range: ${format(new Date(startDate), 'MMM d, yyyy')} - ${format(new Date(endDate), 'MMM d, yyyy')}`, 105, 35, { align: 'center' });
  doc.text(`Squads: ${selectedSquads.join(', ')}`, 105, 42, { align: 'center' });
  
  let yPosition = 50;

  // Generate section for each squad
  playerStats.forEach((squadStats, index) => {
    // Add squad header
    doc.setFontSize(14);
    doc.setTextColor(0, 40, 85);
    doc.text(`${squadStats.squad} Squad`, 14, yPosition);
    
    yPosition += 10;

    // Add summary statistics
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`Total Sessions: ${squadStats.stats.totalSessions}`, 14, yPosition);
    yPosition += 8;
    doc.text(`Total Training Hours: ${squadStats.stats.totalHours.toFixed(1)}`, 14, yPosition);
    yPosition += 8;
    doc.text(`Average Attendance: ${((squadStats.stats.presentSessions / squadStats.stats.totalSessions) * 100).toFixed(1)}%`, 14, yPosition);
    
    yPosition += 15;

    // Add player attendance table
    const tableData = squadStats.stats.players.map((player: any) => [
      player.name,
      player.present,
      player.absent,
      `${((player.present / (player.present + player.absent)) * 100).toFixed(1)}%`,
      `${player.totalHours.toFixed(1)} hours`
    ]);

    // @ts-ignore - jspdf-autotable types
    doc.autoTable({
      startY: yPosition,
      head: [['Player Name', 'Present', 'Absent', 'Attendance %', 'Training Hours']],
      body: tableData,
      headStyles: { fillColor: [0, 40, 85], textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [240, 240, 240] },
      margin: { top: 10 }
    });

    // @ts-ignore - jspdf-autotable types
    yPosition = doc.lastAutoTable.finalY + 20;

    // Add new page if needed
    if (yPosition > 250 && index < playerStats.length - 1) {
      doc.addPage();
      yPosition = 20;
      // Add logo to new page
      addLogoToPDF(doc);
    }
  });

  // Save the PDF
  doc.save(`MBFC_combined_attendance_${format(new Date(), 'yyyyMMdd')}.pdf`);
};